import { useCallback } from 'react';
import { authService } from '../../../services/authService';
import { useSnackbar } from '../../../components/common/Snackbar';
import { validatePasswordChange, extractErrorMessage } from '../../../utils/settingsUtils';
import { DEFAULT_PASSWORD_DATA, DEFAULT_SHOW_PASSWORDS } from '../../../constants/settingsPage';

/**
 * Hook to manage password change logic for Settings page
 * @param {Object} params - Hook parameters
 * @param {Object} params.passwordData - Password data object
 * @param {Function} params.setPasswordData - Setter for password data
 * @param {Function} params.setShowPasswords - Setter for password visibility
 * @param {Function} params.setIsChangingPassword - Setter for changing state
 * @returns {Object} Password change handlers
 */
export const useSettingsPassword = ({
  passwordData,
  setPasswordData,
  setShowPasswords,
  setIsChangingPassword,
}) => {
  const { showSnackbar } = useSnackbar();

  const handleChangePassword = useCallback(async () => {
    if (!validatePasswordChange(passwordData, showSnackbar)) {
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await authService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );

      // Show success message
      const successMessage = response?.message || 'Password changed successfully';
      showSnackbar(successMessage, 'success');

      // Clear password fields
      setPasswordData(DEFAULT_PASSWORD_DATA);

      // Reset password visibility
      setShowPasswords(DEFAULT_SHOW_PASSWORDS);
    } catch (error) {
      const errorMessage = extractErrorMessage(error, 'Failed to change password');
      showSnackbar(errorMessage, 'error');
      // console.error('Error changing password:', error);
    } finally {
      setIsChangingPassword(false);
    }
  }, [passwordData, setPasswordData, setShowPasswords, setIsChangingPassword, showSnackbar]);

  const handleClearPassword = useCallback(() => {
    setPasswordData(DEFAULT_PASSWORD_DATA);
    setShowPasswords(DEFAULT_SHOW_PASSWORDS);
  }, [setPasswordData, setShowPasswords]);

  return {
    handleChangePassword,
    handleClearPassword,
  };
};
